import { defineStore } from 'pinia'
import { useAuthStore } from '@sto/auth.store'

import * as VehicleService from '@ser/vehicle'
import * as AssetService from '@ser/asset'

import { shallowRef } from 'vue'

import VehicleCreateStepVehicle from '@par/vehicle/VehicleCreateStepVehicle.vue'
import VehicleCreateStepValuation from '@par/vehicle/VehicleCreateStepValuation.vue'
import VehicleCreateStepGrade from '@par/vehicle/VehicleCreateStepGrade.vue'
import VehicleCreateVehicleStepLocation from '@par/vehicle/VehicleCreateVehicleStepLocation.vue'
import VehicleCreateStepImages from '@par/vehicle/VehicleCreateStepImages.vue'
import VehicleCreateVehicleStepAuction from '@par/vehicle/VehicleCreateVehicleStepAuction.vue'

export const useVehicleStore = defineStore('vehicle', {

    state: () => {
        return {
            vehicle: null,
            vehicle_events: [],
            vehicle_records: [],
            vehicle_error_heading: '',
            vehicle_error_message: '',
            vehicle_loaded: false,
            active_step_id: 'vehicle',
            _user_is_auctioneer: true,
            _user_is_seller: true,
            create_steps_base: [
                {
                    id: 'vehicle',
                    name: 'Vehicle Information',
                    description: 'Find the vehicle and enter its details',
                    complete: false,
                    status: 'current',
                    scope: '*',
                    component: shallowRef(VehicleCreateStepVehicle)
                },
                {
                    id: 'images',
                    name: 'Photos',
                    description: '(Optional) Add photos for this vehicle',
                    complete: false,
                    status: 'upcoming',
                    scope: '*',
                    component: shallowRef(VehicleCreateStepImages)
                },
                {
                    id: 'valuation',
                    name: 'Valuation',
                    description: '(Optional) Vehicle valuations',
                    complete: false,
                    status: 'upcoming',
                    scope: '*',
                    component: shallowRef(VehicleCreateStepValuation)
                },
                {
                    id: 'grade',
                    name: 'Listing',
                    description: '(Optional) Vehicle information',
                    complete: false,
                    status: 'upcoming',
                    scope: '*',
                    component: shallowRef(VehicleCreateStepGrade)
                },
                {
                    id: 'auctioneer',
                    name: 'Auctioneer',
                    description: '(Optional) Auction settings',
                    complete: false,
                    status: 'upcoming',
                    scope: 'auctioneer',
                    component: shallowRef(VehicleCreateVehicleStepAuction)
                },
                {
                    id: 'location',
                    name: 'Location',
                    description: '(Optional) Where is the vehicle now?',
                    complete: false,
                    status: 'upcoming',
                    scope: '*',
                    component: shallowRef(VehicleCreateVehicleStepLocation)
                }
            ],
            create_steps_complete_base: false
        }
    },

    getters: {
        create_steps: state => state.create_steps_base,
        create_steps_complete: state => state.create_steps_complete_base,
        current_active_step: state => {
            let current_step_index = state.create_steps_base.findIndex(s => s.status == 'current')
            current_step_index = current_step_index ?? state.create_steps_base - 1
            return state.create_steps_base[current_step_index].id

        },
        user_is_auctioneer: state => state._user_is_auctioneer,
        user_is_seller: state => state._user_is_seller,
    },

    actions: {

        resetStore() {
            this.vehicle = null
            this.vehicle_events = []
            this.vehicle_records = []
            this.vehicle_error_heading = ''
            this.vehicle_error_message = ''
            this.vehicle_loaded = false
            this.active_step_id = 'vehicle'
            this.create_steps_base.forEach((step, index) => {
                step.status = index === 0 ? 'current' : 'upcoming'
                step.complete = false
            })
            this.create_steps_complete_base = false
        },

        setCreateModalState(open) {
            this.create_steps_complete_base = open
        },

        setStepsState(vehicle) {
            
            if (vehicle?.vin) {
                const vehicle_step_index = this.create_steps_base.findIndex(s => s.id == 'vehicle')
                this.create_steps_base[vehicle_step_index].status = 'complete'
                this.create_steps_base[vehicle_step_index].complete = true
            }

            if (vehicle?.cap_average && vehicle?.stand_in_value) {
                const valuation_step_index = this.create_steps_base.findIndex(s => s.id == 'valuation')
                this.create_steps_base[valuation_step_index].status = 'complete'
                this.create_steps_base[valuation_step_index].complete = true

                // applies to step after images
                const images_step_index = this.create_steps_base.findIndex(s => s.id == 'images')
                this.create_steps_base[images_step_index].status = 'complete'
                this.create_steps_base[images_step_index].complete = true
            }

            if (vehicle?.asset?.dealer_grade) {
                const grade_step_index = this.create_steps_base.findIndex(s => s.id == 'grade')
                this.create_steps_base[grade_step_index].status = 'complete'
                this.create_steps_base[grade_step_index].complete = true
            }

            const first_incomplete_step = this.create_steps_base.findIndex(s => s.complete == false)
            // activate the first avaiable step OR the final step if all completes
            if (!isNaN(first_incomplete_step) && this.create_steps_base[first_incomplete_step]) {
                this.create_steps_base[first_incomplete_step].status = 'current'
            } else {
                this.create_steps_base[(this.create_steps_base.length - 1)].status = 'current'
            }
        },

        /**
         * determine whether the user is the auctioneer of a given asset
         * @param user
         * @param asset
         * @returns {boolean}
         */
        userIsAssetAuctioneer(user = null, asset = null) {

            if (!user || !asset || !asset.auctioneer_id) return false

            if (!Array.isArray(user.organisation_auctioneers) || !user.organisation_auctioneers.length) return false

            const user_auctioneer_ids = user.organisation_auctioneers.map(({id}) => id)

            return user_auctioneer_ids.length > 0 && user_auctioneer_ids.includes(asset.auctioneer_id)
        },

        /**
         * Determines if a user is the seller of a given asset.
         *
         * @param {object} user - The user object representing the user to check.
         * @param {object} asset - The asset object representing the asset to check against.
         * @returns {boolean} - True if the user is the seller of the asset, false otherwise.
         */
        userIsAssetSeller(user = null, asset = null) {

            if (!user || !asset) return false

            let user_organisation_account_ids = Array.isArray(user.organisation_accounts)
                ? user.organisation_accounts.map(({id}) => id)
                : []
            let user_user_account_ids = Array.isArray(user.user_accounts)
                ? user.user_accounts.map(({id}) => id)
                : []
            let user_account_ids = user_organisation_account_ids.concat(user_user_account_ids)

            return user_account_ids.length > 0 && user_account_ids.includes(asset.seller_account_id)
        },

        /**
         * get vehicle
         * 
         * @param {int} vehicle_id
         * @param {bool} set_state
         */
        async show(vehicle_id, set_state) {

            if (this.vehicle?.id && (this.vehicle?.id != vehicle_id)) this.resetStore()

            const AUTH_STORE = useAuthStore()

            try {
                this.clearVehicleError()
                const vehicle = await VehicleService.show(vehicle_id)

                // user is not the auctioneer, remove the auctioneer steps
                if (!this.userIsAssetAuctioneer(AUTH_STORE.user, vehicle?.asset)) {
                    this._user_is_auctioneer = false
                    this.create_steps_base = this.create_steps_base.filter(step => step.scope !== 'auctioneer')
                }

                // user is not tje seller. remove tje seller steps
                if (!this.userIsAssetSeller(AUTH_STORE.user, vehicle?.asset)) {
                    this._user_is_seller = false
                    this.create_steps_base = this.create_steps_base.filter(step => step.scope !== 'seller')
                }

                if (set_state) this.setStepsState(vehicle)
                this.vehicle = vehicle
                this.vehicle_loaded = true

            } catch (error) {
                console.error('VehicleService.show error', error)
                switch(error?.status) {
                    case 404:
                        this.setVehicleError('Vehicle Not Found', 'We couldn\'t find this vehicle in the system.')
                        break
                    case 403:
                        this.setVehicleError('Permission Denied', 'You do not have permission to edit this vehicle.')
                        break
                    default:
                        this.setVehicleError('Well this is awkward!', 'Sorry, there was an error retrieving this vehicle\'s data.')
                }
            }
        },

        /**
         * delete a vehicle
         */
        async deleteForOrganisation(organisation_id, vehicle_id) {
            return VehicleService.removeOrganisationVehicle(organisation_id, vehicle_id)
        },

        /**
         * update a vehicle
         * 
         * @param {int} vehicle_id
         * @param {obj} payload
         */
         async update(vehicle_id, payload) {
            return VehicleService.update(vehicle_id, payload)
        },

        /**
         * update the asset associated with the vehicle
         * @param {int} asset_id
         * @param {obj} payload
         */
        async updateAsset(asset_id, payload) {
            return AssetService.update(asset_id, payload)
        },

        /**
         * activate a specific step
         * 
         * @param {str} step_id for the target step
         */
        activateStep(step_id) {
            const current_step_index = this.create_steps_base.findIndex(s => s.status == 'current')
            this.create_steps_base[current_step_index].status = this.create_steps_base[current_step_index].complete ? 'complete' : 'upcoming'
            const target_step_index = this.create_steps_base.findIndex(s => s.id == step_id)
            if (!isNaN(target_step_index) && target_step_index >= 0) this.create_steps_base[target_step_index].status = 'current'
        },

        /**
         * activate tne next step in the arrray
         */
        activateNextStep() {
            const current_step_index = this.create_steps_base.findIndex(s => s.status == 'current')
            // complete this step
            this.create_steps_base[current_step_index].complete = true
            this.create_steps_base[current_step_index].status = 'complete'
            // not the final step
            if (current_step_index < this.create_steps_base.length) {
                //this.active_step_id = this.create_steps_base[current_step_index + 1].id
                this.create_steps_base[current_step_index + 1].status = 'current'
            } else {
                // TODO handle final step sceanrio
            }
        },

        /**
         * activate the previous step
         */
        activatePrevStep() {
            const current_step_index = this.create_steps_base.findIndex(s => s.status == 'current')
            // not the first step
            if (current_step_index > 0) {
                this.create_steps_base[current_step_index].status = this.create_steps_base[current_step_index].complete ? 'complete' : 'upcoming'
                this.create_steps_base[current_step_index - 1].status = 'current'
            }
        },

        /**
         * set a loan error
         */
        setVehicleError(heading, message) {
            this.loan_error_heading = heading || ''
            this.loan_error_message = message || ''
        },

        /**
         * clear loan errors
         */
        clearVehicleError() {
            this.setVehicleError()
        },

        /**
         * get the checks for a given vehicle
         */
        async getChecks(vehicle_id, params) {
            return VehicleService.getChecks(vehicle_id, params)
        },

        /**
         * run the chjecks fro a given vehicle id
         */
        async runChecks(vehicle_id) {
            return VehicleService.runChecks(vehicle_id)
        },

        async findVehicleByVrm(vrm) {
            const AUTH_STORE = useAuthStore()
            return VehicleService.vrmCheck(vrm, AUTH_STORE.user?.id)
        }
    }

})